"use client";

import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  GoogleOutlined,
  UserOutlined,
  LockOutlined,
    ArrowLeftOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";

import { Button } from "../../../assets/AntDesign/Button";
import { Input } from "../../../assets/AntDesign/Input";

import { useTranslation } from "react-i18next";
import useLoginPatient from "../../hooks/useLoginPatient";
import { ROUTES } from "../../../routes/patientRoutes/routePaths";
import { useTheme } from "../../../places/context/ThemeContext";
import { OAUTH_URLS } from "../../api/endpoints";
import LanguageSelector from "../../../Landing/components/LanguageSelector/LanguageSelector";
import chooseImage from "../../../assets/icons/tswira.png";
import icon1 from "../../../assets/images/logomedilink.svg";
import "./LoginPatient.css";
import "../AuthLayout.css";

// doit correspondre à la durée de transition CSS (0.5s)
const SWAP_DURATION = 500;
// clé localStorage partagée entre toutes les pages auth (patient/doctor)
const SWAP_STORAGE_KEY = "authHeroOnLeft";
// layout d'origine de cette page : hero à gauche (classe signup-doctor)
const DEFAULT_HERO_ON_LEFT = true;

export default function LoginPatient() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    if (theme !== "light") setTheme("light");
  }, [theme, setTheme]);

  const redirectTo = location.state?.redirectTo || location.state?.from?.pathname;

  const {
    email,
    password,
    submitting,
    errors,
    setEmail,
    setPassword,
    handleLogin,
    onKeyDown,
  } = useLoginPatient({ redirectTo });

  // ── état persistant de la position des cartes (hero à gauche ou à droite) ──
  const initialHeroOnLeft = (() => {
    if (typeof location.state?.heroOnLeft === "boolean") {
      return location.state.heroOnLeft;
    }
    const stored = typeof window !== "undefined" ? localStorage.getItem(SWAP_STORAGE_KEY) : null;
    return stored === null ? DEFAULT_HERO_ON_LEFT : stored === "true";
  })();

  const [heroOnLeft, setHeroOnLeft] = useState(initialHeroOnLeft);

  // ── refs + état pour le swap des 2 cards (form <-> hero) ──
  const heroRef = useRef(null);
  const formWrapperRef = useRef(null);
  const [isSwapping, setIsSwapping] = useState(false);
  const [swapStyle, setSwapStyle] = useState({ form: 0, hero: 0 });

  const handleContinueAsDoctor = () => {
    if (isSwapping) return;

    const formEl = formWrapperRef.current;
    const heroEl = heroRef.current;

    if (formEl && heroEl) {
      const formRect = formEl.getBoundingClientRect();
      const heroRect = heroEl.getBoundingClientRect();
      const formDeltaX = heroRect.left - formRect.left;
      const heroDeltaX = formRect.left - heroRect.left;
      setSwapStyle({ form: formDeltaX, hero: heroDeltaX });
    }

    setIsSwapping(true);

    const newHeroOnLeft = !heroOnLeft;

    setTimeout(() => {
      localStorage.setItem(SWAP_STORAGE_KEY, String(newHeroOnLeft));
      navigate("/LoginDoctor", { state: { heroOnLeft: newHeroOnLeft } });
    }, SWAP_DURATION);
  };

  return (
    <div className="signup-container login-page">
      <div
        className={`signup-background signup-doctor login-background ${
          heroOnLeft ? "pos-hero-left" : "pos-form-left"
        }`}
      >
        <aside
          className="signup-hero"
          ref={heroRef}
          style={{
            backgroundImage: `url(${chooseImage})`,
            ...(isSwapping
              ? { transform: `translateX(${swapStyle.hero}px)`, opacity: 0.15 }
              : {}),
          }}
        >
          <div className="signup-hero-glow" />
          <button
            type="button"
            className="signup-back-home"
            onClick={() => navigate(ROUTES.LANDING)}
            aria-label={t("common.backToHome", "Back to home page")}
          >
             <ArrowLeftOutlined />
            <span>{t("common.backToHome", "Back to home page")}</span>
          </button>
          <div className="signup-hero-top-icon" aria-hidden="true">
            <img src={icon1} alt="decorative icon" />
          </div>
          <div className="signup-hero-inner">
            <h2 className="signup-hero-title">
              {t("signup.heroPanelTitle", "Are you joining as a healthcare professional?")}
            </h2>
            <p className="signup-hero-sub">
              {t("signup.heroPanelText", "This registration is designed for patients. If you are a doctor and wish to manage your practice, schedule appointments, and connect with patients, please ")}
              <strong>{t("signup.heroPanelTextStrong", "create a doctor account.")}</strong>
            </p>
            <button
              type="button"
              className="signup-hero-cta"
              onClick={handleContinueAsDoctor}
            >
              <span>{t("signup.continueAsDoctor", "Continue as a doctor")}</span>
              <ArrowRightOutlined />
            </button>
          </div>
        </aside>

        <section
          className="signup-form-wrapper"
          ref={formWrapperRef}
          style={
            isSwapping
              ? { transform: `translateX(${swapStyle.form}px)`, opacity: 0.15 }
              : undefined
          }
        >
          <div className="signup-form login-form" onKeyDown={onKeyDown}>
            <div className="signup-form-header">
              <h1 className="signup-form-title-main">
                {t("auth.signInPrefix", "Sign In")}{" "}
                <span className="signup-form-title-accent">
                  {t("auth.signInAccentPatient", "as a Patient")}
                </span>
              </h1>
              <p className="signup-form-subtitle">{t("auth.signInSubtitle")}</p>
            </div>

            <div className="signup-form-content">
              <div className="login-form-group">
                <label className="login-form-label">{t("common.email")}</label>
                <Input
                  placeholder={t("common.emailPlaceholder")}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="login-form-input"
                  prefix={<UserOutlined />}
                  autoComplete="username"
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <span className="login-error-message">{errors.email}</span>
                )}
              </div>

              <div className="login-form-group">
                <label className="login-form-label">{t("common.password")}</label>
                <Input.Password
                  placeholder={t("common.passwordPlaceholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="login-form-input"
                  prefix={<LockOutlined />}
                  autoComplete="current-password"
                  aria-invalid={!!errors.password}
                />
                {errors.password && (
                  <span className="login-error-message">{errors.password}</span>
                )}
              </div>

              {errors.general && (
                <span className="login-error-message">{errors.general}</span>
              )}

              <Button
                onClick={handleLogin}
                className="login-btn"
                type="primary"
                loading={submitting}
                disabled={submitting}
              >
                <span>{t("auth.signInButton")}</span>
               
              </Button>

              <div className="login-forgot-password">
                <button
                  type="button"
                  className="login-forgot-link"
                  onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
                >
                  {t("auth.forgotPassword")}
                </button>
              </div>

              <div className="login-divider">
                <span>{t("common.or")}</span>
              </div>

              <div className="login-social-buttons">
                <a href={OAUTH_URLS.GOOGLE}>
                  <Button icon={<GoogleOutlined />} className="login-google-btn">
                    {t("auth.signInWithGoogle")}
                  </Button>
                </a>
              </div>

              <div className="login-signup-redirect">
                {t("auth.needAccount")}{" "}
                <button
                  type="button"
                  className="login-signup-link"
                  onClick={() => navigate(ROUTES.SIGNUP_PATIENT)}
                >
                  {t("auth.createOne")}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
